import cx from 'clsx';
import React, { FunctionComponent, useRef, useState } from 'react';
import styles from './ContactForm.module.scss';
import { WPAcfContactForm, WPAcfNewsletterForm } from 'types/acf/forms';
import { EmbedForm, Block, BlockContent, BlockHeader, Icon } from 'components/elements';
import { WPAcfKnowledgeResource } from 'types/acf/knowledge';
import SingleOpinion from 'components/presentional/SingleOpinion/SingleOpinion';
import useNewIntersectionObserver from 'utils/intersection';
import { Cross, Newsletter } from 'assets/icons';
import { LazyComponent } from 'utils';

interface OwnProps {
  name: string;
  data: WPAcfContactForm | WPAcfNewsletterForm | WPAcfKnowledgeResource;
  sticky?: boolean;
  initialyOpened?: boolean;
  theme?: 'dark' | 'white';
}

type ContactFormProps = Omit<JSX.IntrinsicElements['div'], 'children' | 'name'> & OwnProps;

type GRFormProps = Omit<JSX.IntrinsicElements['div'], 'children' | 'name'> & OwnProps;

const GRForm: FunctionComponent<GRFormProps> = props => {
  const { className, data, sticky = false, theme = 'dark', initialyOpened = false, name, ...rest } = props;
  const { formId, message, embed } = data;
  const previousOpenedState = localStorage.getItem('sticky-form');
  const openedState = previousOpenedState !== null ? previousOpenedState === 'true' : initialyOpened;
  const [opened, setOpened] = useState(openedState);

  const formClassNames = cx({
    [styles.form]: true,
    [styles.sticky]: sticky,
    [styles.opened]: sticky && opened,
  });

  const contactFormClassNames = cx({
    [styles.wrapper]: true,
    ...(className && { [className]: true }),
  });

  const onClick = () => {
    localStorage.setItem('sticky-form', `${!opened}`);
    setOpened(!opened);
  };

  return (
    <div className={formClassNames}>
      <LazyComponent>
        {sticky && (
          <div data-element-id="sticky-button" onClick={onClick} className={styles.openButton}>
            {opened && <Icon icon={Cross} size={20} />}
            {!opened && <Icon icon={Newsletter} size={20} />}
          </div>
        )}
        <div className={styles.overlay}>
          {'header' in data && data.header && <BlockHeader className={styles.header}>{data.header}</BlockHeader>}
          <div className={contactFormClassNames} {...rest}>
            <EmbedForm name={name} small={sticky && opened} message={message} embed={embed} theme={theme} formId={formId} />
          </div>
        </div>
      </LazyComponent>
    </div>
  );
};

const ContactForm: FunctionComponent<ContactFormProps> = props => {
  const { data, id, name, theme = 'dark', sticky, initialyOpened } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useNewIntersectionObserver(ref, {});

  const containerClassNames = cx({
    [styles.container]: true,
    [styles[theme]]: true,
  });

  return (
    <Block theme={theme} data-module={name}>
      <BlockContent ref={ref} id={id} className={containerClassNames} direction="row">
        <GRForm {...props} initialyOpened={initialyOpened} sticky={sticky && !entry?.isIntersecting}/>

        {'opinions' in data && data.opinions && data.opinions.items && data.opinions.items.length > 0 && (
            <div className={styles.opinions}>
              {data?.opinions?.header &&
                  <BlockHeader className={styles.opinionsHeader}>{data.opinions.header}</BlockHeader>}
              <SingleOpinion opinion={data.opinions.items[0]}/>
            </div>
        )}
      </BlockContent>
    </Block>
  );
};

export default ContactForm;
